/* Hero scroll animation — a scroll-TRIGGERED, time-based play-through.
   The hero pins on load and the page is locked. A downward scroll plays the
   frame sequence forward (start→end) over a fixed, eased duration, then the
   page unlocks and scrolling continues. Scrolling back up to the top plays it
   in reverse (end→start), leaving the start frame armed for a forward replay.
   (Previously this was a scroll-scrubber: frame glued to scroll position.)
   Port target: assets/hero-scroll.js in the Dawn theme, unchanged. Config via
   a <script type="application/json" data-hero-config> block. */
(() => {
  const instances = new Map();

  // Cubic ease-in-out — slow start, quick middle, gentle settle.
  const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  // Keys that scroll the page — all blocked while the hero is locked.
  const SCROLL_KEYS = new Set([
    'ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ', 'Spacebar',
  ]);
  // The downward subset that also starts the play-through.
  const TRIGGER_KEYS = new Set(['ArrowDown', 'PageDown', 'End', ' ', 'Spacebar']);

  class HeroScrollAnimation {
    constructor(root) {
      this.root = root;
      this.cfg = JSON.parse(root.querySelector('[data-hero-config]').textContent);
      this.canvas = root.querySelector('[data-canvas]');
      this.ctx = this.canvas.getContext('2d', { alpha: false });
      this.poster = root.querySelector('[data-poster]');
      this.posterSource = root.querySelector('[data-poster-source]');
      this.fadeouts = [...root.querySelectorAll('[data-fadeout]')];
      this.fadeins = [...root.querySelectorAll('[data-fadein]')];
      this.stats = [...root.querySelectorAll('[data-stat]')];

      this.frames = [];
      this.currentIndex = -1;
      this.rafId = 0;
      this.locked = false;
      this.pendingPlay = false;
      this.touchY = null;
      this.idleTouchY = null;
      this.state = 'loading'; // loading → armed → playing → done ⇄ (reverse) armed
      this.aborter = new AbortController();

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.reducedMotion) return this.renderStaticFallback();

      // The hero gates the page, so it must always begin at the top on load.
      // Disable the browser's scroll restoration and pin to the top — otherwise
      // a refresh can restore a mid-page position and strand the lock off-screen
      // (notably on mobile: end frame showing, page won't scroll back up).
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      window.scrollTo(0, 0);

      // The set is chosen once at construction; crossing the 767px boundary
      // mid-session (e.g. tablet rotation) requires a reload.
      this.isMobile = window.matchMedia('(max-width: 767px)').matches;
      const sparse =
        this.cfg.useSparseDesktop || new URLSearchParams(location.search).has('sparse');
      this.set = this.isMobile
        ? this.cfg.sets.mobile
        : sparse
          ? this.cfg.sets.desktopSparse
          : this.cfg.sets.desktop;
      this.count = this.set.count;
      this.duration = this.cfg.playDuration ?? 1800;

      this.viewportW = window.innerWidth;
      this.viewportH = window.innerHeight;

      this.setHeaderOffset();
      this.resize();

      // Lock immediately: the hero holds the viewport from load until the
      // play-through it triggers has finished. Watch for the first downward
      // scroll intent to start it (queued if frames are still preloading).
      this.lock(); // locks the page AND listens for the first downward intent
      this.preload().then(() => {
        if (this.state !== 'loading') return;
        this.state = 'armed';
        // Only reveal the canvas once the first frame actually loaded; if the
        // frame set is missing (e.g. frameBase not configured yet), leave the
        // canvas hidden so the poster still shows instead of an opaque canvas.
        if (this.frames[0]) {
          this.draw(0);
          this.canvas.style.opacity = '1'; // the animation frame is the resting image
        }
        this.updateOverlays(0);
        if (this.pendingPlay) this.play(false);
      });

      window.addEventListener('resize', () => this.onResize(), {
        passive: true,
        signal: this.aborter.signal,
      });
      // The theme header's logo <img> has no width/height attributes, so the
      // header can grow after our first measurement — re-measure once every
      // late asset has loaded.
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => {
          this.setHeaderOffset();
          this.resize();
        }, { once: true, signal: this.aborter.signal });
      }
      // Persistent (passive) listeners for the reverse trigger: once the
      // forward play is done, an up-scroll while back at the very top plays the
      // sequence in reverse. They no-op unless state === 'done' (see below).
      const idle = { passive: true, signal: this.aborter.signal };
      window.addEventListener('wheel', (e) => {
        if (e.deltaY < 0) this.onReverseIntent();
      }, idle);
      window.addEventListener('touchstart', (e) => {
        this.idleTouchY = e.touches[0].clientY;
      }, idle);
      window.addEventListener('touchmove', (e) => {
        // Finger moving down (content would scroll up) is a reverse intent.
        if (this.idleTouchY != null && e.touches[0].clientY - this.idleTouchY > 8) {
          this.onReverseIntent();
        }
      }, idle);
      window.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowUp' || e.key === 'PageUp' || e.key === 'Home') this.onReverseIntent();
      }, idle);
    }

    destroy() {
      cancelAnimationFrame(this.rafId);
      clearTimeout(this.failsafe);
      this.unlock();
      this.aborter.abort();
    }

    frameUrl(i) {
      return `${this.cfg.frameBase}/${this.set.prefix}_frame_${String(i + 1).padStart(4, '0')}.webp`;
    }

    renderStaticFallback() {
      if (this.posterSource) this.posterSource.srcset = this.cfg.endStills.mobile;
      this.poster.src = this.cfg.endStills.desktop;
      [...this.stats, ...this.fadeins].forEach((s) => {
        s.style.opacity = 1;
        s.style.transform = 'none';
      });
    }

    // --- Scroll lock + trigger ---------------------------------------------

    // Lock by consuming the scroll input events (preventDefault) rather than
    // mutating document overflow — the document's own scroll state is never
    // touched, so releasing (detaching the listeners) can't leave the page
    // wedged. The same listeners detect the first downward intent to play.
    lock() {
      if (this.locked) return;
      this.locked = true;
      this.lockAborter = new AbortController();
      const block = { passive: false, signal: this.lockAborter.signal };

      window.addEventListener('wheel', (e) => {
        e.preventDefault();
        if (e.deltaY > 0) this.onIntent();
      }, block);
      window.addEventListener('touchstart', (e) => {
        this.touchY = e.touches[0].clientY;
      }, { passive: true, signal: this.lockAborter.signal });
      window.addEventListener('touchmove', (e) => {
        e.preventDefault();
        // Finger moving up (content would scroll down) is a downward intent.
        if (this.touchY != null && this.touchY - e.touches[0].clientY > 8) this.onIntent();
      }, block);
      window.addEventListener('keydown', (e) => {
        if (!SCROLL_KEYS.has(e.key)) return;
        e.preventDefault();
        if (TRIGGER_KEYS.has(e.key)) this.onIntent();
      }, block);
    }

    unlock() {
      if (!this.locked) return;
      this.locked = false;
      if (this.lockAborter) this.lockAborter.abort(); // detach every lock listener
    }

    onIntent() {
      if (this.state === 'armed') this.play(false); // forward: start → end
      else if (this.state === 'loading') this.pendingPlay = true; // play once frames land
    }

    onReverseIntent() {
      // Up-scroll at the very top, after the forward play, reverses the zoom.
      if (this.state === 'done' && window.scrollY <= 4) this.play(true);
    }

    // --- Preload -----------------------------------------------------------

    async preload() {
      const load = (i) =>
        new Promise((resolve) => {
          const img = new Image();
          img.decoding = 'async';
          img.src = this.frameUrl(i);
          img.onload = async () => {
            // Warm the decode cache so the first draw of each frame does not
            // block the main thread mid-play.
            if (img.decode) {
              try {
                await img.decode();
              } catch (e) {}
            }
            this.frames[i] = img;
            resolve();
          };
          img.onerror = () => resolve(); // tolerate gaps — draw() holds the previous frame
        });

      // First and last frames first so the ends are correct as early as
      // possible, then fill in.
      await Promise.all([load(0), load(this.count - 1)]);
      await Promise.all(
        Array.from({ length: this.count }, (_, i) => i)
          .filter((i) => i !== 0 && i !== this.count - 1)
          .map(load)
      );
    }

    // --- Sizing ------------------------------------------------------------

    // The host theme renders its header in normal flow above the hero, so the
    // hero must be viewport-height MINUS everything above it. Measure the
    // section's document offset into --lum-header-h (the CSS height calc
    // subtracts it). In the header-less dev prototype this resolves to 0.
    setHeaderOffset() {
      const top = Math.max(0, Math.round(this.root.getBoundingClientRect().top + window.scrollY));
      this.root.style.setProperty('--lum-header-h', `${top}px`);
    }

    resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const { width, height } = this.canvas.getBoundingClientRect();
      this.canvas.width = Math.round(width * dpr);
      this.canvas.height = Math.round(height * dpr);
      // Resizing resets context state; re-request quality resampling for the
      // residual scale between frame pixels and device pixels.
      this.ctx.imageSmoothingQuality = 'high';
      this.currentIndex = -1; // force a redraw
      // Repaint the frame the current state should be showing.
      if (this.state === 'done') this.draw(this.count - 1);
      else if (this.state === 'armed') this.draw(0);
    }

    onResize() {
      if (window.innerWidth === this.viewportW) return; // address bar collapse — ignore
      this.viewportW = window.innerWidth;
      this.viewportH = window.innerHeight;
      this.setHeaderOffset();
      this.resize();
    }

    // --- Play-through ------------------------------------------------------

    // direction false = forward (armed → done, ends unlocked on the end frame);
    //           true  = reverse (done → armed, ends locked on the start frame).
    play(reverse) {
      if (reverse ? this.state !== 'done' : this.state !== 'armed') return;
      this.state = 'playing';
      if (reverse) this.lock(); // reverse begins from the unlocked 'done' state
      const startedAt = performance.now();
      // Backstop: guarantee the state resolves even if rAF is throttled (e.g.
      // the tab is backgrounded mid-play) so the page can never stay locked.
      this.failsafe = setTimeout(() => this.finish(reverse), this.duration + 400);

      const tick = (now) => {
        const t = Math.min((now - startedAt) / this.duration, 1);
        const eased = easeInOut(t);
        const p = reverse ? 1 - eased : eased; // reverse walks end → start

        const index = Math.round(p * (this.count - 1));
        if (index !== this.currentIndex) {
          this.draw(index);
          this.currentIndex = index;
        }
        this.updateOverlays(p);

        if (t < 1) this.rafId = requestAnimationFrame(tick);
        else this.finish(reverse);
      };
      this.rafId = requestAnimationFrame(tick);
    }

    finish(reverse) {
      const endState = reverse ? 'armed' : 'done';
      if (this.state === endState) return; // idempotent — rAF end and failsafe race
      clearTimeout(this.failsafe);
      cancelAnimationFrame(this.rafId);
      this.state = endState;
      this.currentIndex = reverse ? 0 : this.count - 1;
      this.draw(this.currentIndex);
      this.updateOverlays(reverse ? 0 : 1);
      // The resting image is the animation frame itself — the canvas stays
      // visible, so there's no still swap and no size/sharpness pop.
      // Forward ends on the end frame and releases the page. Reverse ends on the
      // start frame and STAYS locked — same as initial load, so the next
      // downward scroll replays forward.
      if (!reverse) this.unlock();
    }

    draw(index) {
      const img = this.frames[index];
      if (!img) return; // gap — hold the previous frame

      // Canvas has no object-fit, so replicate `cover` manually.
      const cw = this.canvas.width;
      const ch = this.canvas.height;
      const scale = Math.max(cw / img.width, ch / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      // Mobile anchors 30% from the top (same as the CSS object-position on
      // the poster) so short viewports crop the counter, not the headline gap.
      const dy = this.isMobile ? (ch - h) * 0.3 : (ch - h) / 2;
      this.ctx.drawImage(img, (cw - w) / 2, dy, w, h);
    }

    // --- Overlays ----------------------------------------------------------

    updateOverlays(p) {
      // Headline and trust bar fade out over the first 20% of the play-through.
      const fade = String(1 - Math.min(p / 0.2, 1));
      this.fadeouts.forEach((el) => {
        el.style.opacity = fade;
        // Keep faded-out overlays from blocking taps/clicks on the canvas area
        el.style.pointerEvents = p > 0.2 ? 'none' : '';
      });

      // Fade-ins (the description) rise in over the last 30%, once the camera
      // has arrived — cross-fading with the title that just faded out up top.
      const fadeIn = Math.min(Math.max((p - 0.7) / 0.2, 0), 1);
      this.fadeins.forEach((el) => {
        el.style.opacity = String(fadeIn);
        el.style.transform = `translateY(${(1 - fadeIn) * 12}px)`;
      });

      // Stats stagger in over the last 25%, once the camera has arrived.
      this.stats.forEach((el, i) => {
        const start = 0.75 + i * 0.06;
        const t = Math.min(Math.max((p - start) / 0.12, 0), 1);
        el.style.opacity = String(t);
        el.style.transform = `translateY(${(1 - t) * 12}px)`;
      });
    }
  }

  const init = (scope) => {
    (scope || document).querySelectorAll('[data-hero-scroll]').forEach((el) => {
      if (!instances.has(el)) instances.set(el, new HeroScrollAnimation(el));
    });
  };

  const teardown = (scope) => {
    (scope || document).querySelectorAll('[data-hero-scroll]').forEach((el) => {
      const instance = instances.get(el);
      if (instance) {
        instance.destroy();
        instances.delete(el);
      }
    });
  };

  init();

  // Shopify theme editor re-renders sections without a page reload.
  // No-ops outside Shopify.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('shopify:section:unload', (e) => teardown(e.target));
})();
